// v1.22.0 · Auditoría · Banderas rojas: filtro, detalle con evidencia, aclaraciones (nota + archivos), recomendación de la IA y estado
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Bot, FileUp, Flag, Loader2, Paperclip, Sparkles, Trash2, X } from 'lucide-react';
import { C, SEV, STATUS, api, download, errMsg, fdate, money, plab } from './api';
import { SevTag } from './Panel';

export default function Banderas({ cid, analysis, filters, setFilters, openKey, setOpenKey, onChanged }) {
  const flags = useMemo(() => analysis?.flags || [], [analysis]);
  const areas = useMemo(() => [...new Set(flags.map((f) => f.area))], [flags]);
  const f = filters;
  const list = flags.filter((x) => (!f.severity || x.severity === f.severity) && (!f.area || x.area === f.area)
    && (!f.status || x.status === f.status) && (!f.q || `${x.title} ${x.account} ${x.code} ${x.detail}`.toLowerCase().includes(f.q.toLowerCase())));
  const flag = flags.find((x) => x.key === openKey);
  return (
    <div className="neu-grid" style={{ gap: 18 }}>
      <section className="neu-card">
        <div className="neu-row">
          <Flag size={18} color={SEV.alta.color} /><p className="neu-title" style={{ fontSize: 15 }}>Banderas rojas ({list.length} de {flags.length})</p>
          <span className="neu-spacer" />
          {['', 'alta', 'media', 'baja'].map((s) => (
            <button type="button" key={s || 'todas'} className={`neu-btn neu-btn-sm ${f.severity === s ? 'is-on' : ''}`} onClick={() => setFilters({ ...f, severity: s })}>
              {s ? <SevTag s={s} /> : 'Todas'}
            </button>
          ))}
        </div>
        <div className="neu-grid neu-grid-3" style={{ marginTop: 12, gap: 10 }}>
          <select className="neu-select" value={f.area || ''} onChange={(e) => setFilters({ ...f, area: e.target.value })} aria-label="Área">
            <option value="">Todas las áreas</option>{areas.map((a) => <option key={a} value={a}>{a}</option>)}</select>
          <select className="neu-select" value={f.status || ''} onChange={(e) => setFilters({ ...f, status: e.target.value })} aria-label="Estado">
            <option value="">Todos los estados</option>{Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>
          <input className="neu-input" placeholder="Buscar cuenta o texto" value={f.q || ''} onChange={(e) => setFilters({ ...f, q: e.target.value })} />
        </div>
        <p className="neu-hint" style={{ marginTop: 10 }}>
          Ordenadas de lo macro a lo micro (general, activos, pasivos, patrimonio, ingresos, gastos y plan de cuentas). Abra una bandera para subir
          información aclaratoria y pedir a la IA una recomendación con esa información.
        </p>
      </section>
      <div className="neu-grid" style={{ gap: 12 }}>
        {list.map((x) => (
          <button type="button" key={x.key} className="neu-card" onClick={() => setOpenKey(x.key)}
                  style={{ border: 0, textAlign: 'left', cursor: 'pointer', padding: 16, borderLeft: `5px solid ${SEV[x.severity].color}` }}>
            <div className="neu-row" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div className="neu-row" style={{ gap: 8 }}><SevTag s={x.severity} /><span className="neu-sub">{x.area}</span></div>
                <p style={{ margin: '6px 0 4px', fontWeight: 700, fontSize: 14, color: C.ink }}>{x.title}</p>
                <p className="neu-hint" style={{ margin: 0 }}>{x.detail.length > 220 ? `${x.detail.slice(0, 220)}…` : x.detail}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                {x.amount ? <div className="tabular" style={{ fontWeight: 800, fontSize: 15 }}>{money(x.amount)}</div> : null}
                <div className="neu-row" style={{ justifyContent: 'flex-end', marginTop: 6, gap: 6 }}>
                  {x.clarifications > 0 && <span className="neu-chip neu-chip-gray"><Paperclip size={11} /> {x.clarifications}</span>}
                  {x.ai && <span className="neu-chip neu-chip-gray"><Sparkles size={11} /> IA</span>}
                  <span className={`neu-chip ${STATUS[x.status]?.cls}`}>{STATUS[x.status]?.label}</span>
                </div>
              </div>
            </div>
          </button>
        ))}
        {!list.length && <div className="neu-card neu-empty">No hay banderas con este filtro.</div>}
      </div>
      {flag && <FlagDrawer cid={cid} flag={flag} onClose={() => setOpenKey(null)} onChanged={onChanged} />}
    </div>
  );
}

function FlagDrawer({ cid, flag, onClose, onChanged }) {
  const [clar, setClar] = useState([]);
  const [note, setNote] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [ai, setAi] = useState(flag.ai || null);
  const [thinking, setThinking] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([api.get(`/companies/${cid}/clarifications`, { flag_key: flag.key }),
        flag.code ? api.get(`/companies/${cid}/clarifications`, { account_code: flag.code }) : Promise.resolve([])]);
      const seen = new Set();
      setClar([...a, ...b].filter((c) => !seen.has(c.id) && seen.add(c.id)));
    } catch (e) { toast.error(errMsg(e)); }
  }, [cid, flag.key, flag.code]);
  useEffect(() => { load(); setAi(flag.ai || null); }, [load, flag.ai]);
  useEffect(() => { const k = (e) => e.key === 'Escape' && onClose(); window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);

  const setStatus = async (status, severity) => {
    try { await api.patch(`/companies/${cid}/flags/${flag.key}`, { ...(status ? { status } : {}), ...(severity !== undefined ? { severity } : {}) }); onChanged(); toast.success('Actualizado'); } catch (e) { toast.error(errMsg(e)); }
  };
  const addClar = async (asAccount) => {
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('note', note);
      fd.append('flag_key', asAccount ? '' : flag.key);
      fd.append('account_code', asAccount ? flag.code : '');
      files.forEach((x) => fd.append('files', x));
      await api.form(`/companies/${cid}/clarifications`, fd);
      setNote(''); setFiles([]); toast.success('Aclaración guardada'); load(); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
    setSending(false);
  };
  const askAI = async () => {
    setThinking(true);
    try { const r = await api.post(`/companies/${cid}/flags/${flag.key}/ai`); setAi(r); onChanged(); } catch (e) { toast.error(errMsg(e)); }
    setThinking(false);
  };
  const ev = (flag.evidence || []).filter((e) => e && typeof e === 'object');
  const cols = [...new Set(ev.flatMap((e) => Object.keys(e)))].slice(0, 7);

  return (
    <div className="neu-drawer-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="neu-drawer" role="dialog" aria-modal="true" aria-label={flag.title}>
        <div className="neu-row" style={{ alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}><SevTag s={flag.severity} /> <span className="neu-sub">· {flag.area}</span>
            <h2 className="neu-h1" style={{ fontSize: 19, marginTop: 6 }}>{flag.title}</h2>
            {flag.code && <p className="neu-sub">Cuenta {flag.code} {flag.account}</p>}</div>
          <button type="button" className="neu-btn neu-icon-btn" onClick={onClose} aria-label="Cerrar"><X size={16} /></button>
        </div>

        <div className="neu-card" style={{ marginTop: 14 }}>
          <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55 }}>{flag.detail}</p>
          {flag.amount ? <p className="tabular" style={{ fontWeight: 800, fontSize: 18, margin: '10px 0 0' }}>{money(flag.amount)}</p> : null}
          {flag.criterio && <p className="neu-hint" style={{ marginTop: 8 }}><b>Criterio:</b> {flag.criterio}</p>}
          <p className="neu-hint" style={{ marginTop: 4 }}><b>Recomendación base:</b> {flag.recomendacion}</p>
        </div>

        {ev.length > 0 && (
          <div className="neu-card" style={{ marginTop: 16 }}>
            <p className="neu-title">Evidencia ({ev.length})</p>
            <div className="neu-scroll" style={{ maxHeight: 300 }}>
              <table className="neu-table"><thead><tr>{cols.map((c) => <th key={c} className={typeof ev[0][c] === 'number' ? 'num' : ''}>{c.replace(/_/g, ' ')}</th>)}</tr></thead>
                <tbody>{ev.map((e, i) => <tr key={i}>{cols.map((c) => <td key={c} className={typeof e[c] === 'number' ? 'num' : ''} style={c === 'period' ? { whiteSpace: 'nowrap' } : undefined}>
                  {typeof e[c] === 'number' ? money(e[c]) : c === 'period' ? plab(e[c]) : String(e[c] ?? '')}</td>)}</tr>)}</tbody></table>
            </div>
          </div>
        )}

        <div className="neu-card" style={{ marginTop: 16 }}>
          <p className="neu-title">Estado</p>
          <div className="neu-row" style={{ marginTop: 8 }}>
            {Object.entries(STATUS).map(([k, v]) => <button type="button" key={k} className={`neu-btn neu-btn-sm ${flag.status === k ? 'is-on' : ''}`} onClick={() => setStatus(k)}>{v.label}</button>)}
          </div>
          <div className="neu-row" style={{ marginTop: 10 }}>
            <span className="neu-label">Severidad</span>
            {['alta', 'media', 'baja'].map((s) => <button type="button" key={s} className={`neu-btn neu-btn-sm ${flag.severity === s ? 'is-on' : ''}`} onClick={() => setStatus(null, s)}><SevTag s={s} /></button>)}
            {flag.severity_override && <button type="button" className="neu-btn neu-btn-sm" onClick={() => setStatus(null, '')}>Volver a la calculada</button>}
          </div>
        </div>

        <div className="neu-card" style={{ marginTop: 16 }}>
          <p className="neu-title">Información aclaratoria</p>
          <p className="neu-hint">Explique la partida y adjunte el anexo, la conciliación, el contrato o el estado de cuenta que la respalda. La IA lee el texto de los archivos.</p>
          <textarea className="neu-textarea" style={{ marginTop: 8 }} placeholder="Ej.: la diferencia corresponde a depósitos de estudiantes identificados el 5 de octubre; se adjunta el detalle."
                    value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="neu-row" style={{ marginTop: 10 }}>
            <label className="neu-btn neu-btn-sm" style={{ cursor: 'pointer' }}><FileUp size={14} /> Adjuntar archivos
              <input type="file" multiple hidden onChange={(e) => setFiles(Array.from(e.target.files).slice(0, 5))} /></label>
            <span className="neu-hint">{files.map((x) => x.name).join(', ')}</span><span className="neu-spacer" />
            {flag.code && <button type="button" className="neu-btn neu-btn-sm" disabled={sending || (!note.trim() && !files.length)} onClick={() => addClar(true)} title="Queda en la cuenta y sirve para todas sus banderas">Guardar en la cuenta</button>}
            <button type="button" className="neu-btn neu-btn-sm neu-btn-primary" disabled={sending || (!note.trim() && !files.length)} onClick={() => addClar(false)}>
              {sending ? <Loader2 size={14} className="animate-spin" /> : null} Guardar aclaración</button>
          </div>
          {clar.map((c) => (
            <div key={c.id} className="neu-inset" style={{ marginTop: 10 }}>
              <div className="neu-row"><span className="neu-sub">{c.created_by} · {fdate(c.created_at)}{c.account_code ? ` · cuenta ${c.account_code}` : ''}</span><span className="neu-spacer" />
                <button type="button" className="neu-btn neu-icon-btn neu-btn-danger" aria-label="Borrar aclaración"
                        onClick={async () => { if (!window.confirm('¿Borrar esta aclaración?')) return; await api.del(`/clarifications/${c.id}`); load(); onChanged(); }}><Trash2 size={13} /></button></div>
              {c.note && <p style={{ margin: '6px 0 0', fontSize: 13 }}>{c.note}</p>}
              {(c.files || []).map((x) => <button type="button" key={x.file_id} className="neu-btn neu-btn-sm" style={{ marginTop: 6, marginRight: 6 }}
                                                  onClick={() => download(`/files/${x.file_id}/download`, x.name)}><Paperclip size={12} /> {x.name}</button>)}
            </div>
          ))}
        </div>

        <div className="neu-card" style={{ marginTop: 16 }}>
          <div className="neu-row"><Bot size={18} color={C.brand} /><p className="neu-title">Recomendación de la IA</p><span className="neu-spacer" />
            <button type="button" className="neu-btn neu-btn-sm neu-btn-primary" onClick={askAI} disabled={thinking}>
              {thinking ? <><Loader2 size={14} className="animate-spin" /> Analizando…</> : <><Sparkles size={14} /> {ai ? 'Volver a analizar' : 'Analizar con IA'}</>}</button></div>
          {!ai && !thinking && <p className="neu-hint" style={{ marginTop: 8 }}>La IA revisa la bandera, su evidencia, los saldos de la cuenta y las aclaraciones cargadas.</p>}
          {ai && (
            <div style={{ marginTop: 10, fontSize: 13.5, lineHeight: 1.55 }}>
              <p style={{ margin: 0 }}>{ai.analisis}</p>
              {ai.recomendaciones?.length > 0 && <><p className="neu-label" style={{ marginTop: 12 }}>Recomendaciones</p><ul style={{ margin: '4px 0 0 18px', padding: 0 }}>{ai.recomendaciones.map((r, i) => <li key={i}>{r}</li>)}</ul></>}
              {ai.ajuste_sugerido && <><p className="neu-label" style={{ marginTop: 12 }}>Ajuste sugerido</p><p className="neu-inset" style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{ai.ajuste_sugerido}</p></>}
              {ai.informacion_faltante?.length > 0 && <><p className="neu-label" style={{ marginTop: 12 }}>Información que falta</p><ul style={{ margin: '4px 0 0 18px', padding: 0 }}>{ai.informacion_faltante.map((r, i) => <li key={i}>{r}</li>)}</ul></>}
              {ai.aclaracion_plan_cuentas && <p className="neu-hint" style={{ marginTop: 10 }}><b>Plan de cuentas:</b> {ai.aclaracion_plan_cuentas}</p>}
              <div className="neu-row" style={{ marginTop: 12 }}>
                <span className="neu-hint">Sugiere: estado «{STATUS[ai.estado_sugerido]?.label}», severidad <SevTag s={ai.severidad_sugerida} />. {ai.at ? fdate(ai.at) : ''}</span><span className="neu-spacer" />
                {ai.estado_sugerido !== flag.status && <button type="button" className="neu-btn neu-btn-sm" onClick={() => setStatus(ai.estado_sugerido)}>Aplicar estado sugerido</button>}
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
